import type { ComponentChildren } from 'preact';
import { createContext } from 'preact';
import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'preact/hooks';

type OnCloseCallback = (() => void) | null;

interface DialogContextValue {
  open: boolean;
  setOpen: (value: boolean) => void;
  content: ComponentChildren | null;
  setContent: (value: ComponentChildren | null) => void;
  setOnCloseCallback: (callback: OnCloseCallback) => void;
  closeDialog: () => void; // should be used to close the dialog from an outside source (other than Dialog.Close)
}

const DialogContext = createContext<DialogContextValue | undefined>(undefined);

export function useDialog() {
  const context = useContext(DialogContext);
  if (!context) throw new Error('useDialog must be used within DialogProvider');
  return context;
}

export function DialogProvider({ children }: { children: ComponentChildren }) {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState<ComponentChildren | null>(null);

  const onCloseCallbackRef = useRef<OnCloseCallback>(null);

  const setOnCloseCallback = useCallback((callback: OnCloseCallback) => {
    onCloseCallbackRef.current = callback;
  }, []);

  const resetOnCloseCallback = useCallback(() => {
    onCloseCallbackRef.current = null;
  }, []);

  const closeDialog = () => {
    setOpen(false);
  };

  useEffect(() => {
    const currentCallback = onCloseCallbackRef.current;

    if (!open && currentCallback) {
      console.log('Callback successfully triggered on close.');

      currentCallback();
      resetOnCloseCallback();
    }
  }, [open, resetOnCloseCallback]);

  const store = {
    open,
    setOpen,
    content,
    setContent,
    setOnCloseCallback,
    closeDialog,
  };

  return (
    <DialogContext.Provider value={store}>{children}</DialogContext.Provider>
  );
}
